'use client';

import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import GeoLocationInput from './GeoLocationInput/GeoLocationInput';
import DeprecatedGeoLocationInput from './GeoLocationInput/DeprecatedGeoLocationInput';
import MapLoader from '../MapLoader/MapLoader';
import StoreCards from './StoreCards/StoreCards';
import MapUnavailableMessage from '../MapLoader/MapUnavailableMessage/MapUnavailableMessage';
import useScreenSize from '../../hooks/useScreenSize/useScreenSize';
import { mapFormatStores } from '../MapLoader/mapLoaderUtils/mapLoaderUtils';
import {
  filterStores,
  findClosestStores,
} from './storeFinderUtils/storeFinderUtils';
import { TFormatStore } from '../wrappers/StoreLocatorWrapper/StoreLocatorUtils/StoreLocatorUtils';
import { TStoreCards } from './StoreCards/StoreCards';
import { TServiceOption } from '../wrappers/StoreLocatorWrapper/StoreLocatorWrapper';
import style from './StoreFinder.module.scss';
import { TInformationModal } from '../StoreLocator/StoreLocator';
import { isSafari13OrLower } from '../MapLoader/mapLoaderUtils/mapLoaderClientUtils';
import {
  CLICK_AND_COLLECT,
  BUY_CURRENCY_IN_STORE,
  ALL_TRAVEL_MONEY_STORES,
} from '../../constants/listOptions';

export type TCoordinates = {
  lat: number;
  lng: number;
  address?: string;
};

export type TSelectedService =
  | typeof CLICK_AND_COLLECT
  | typeof BUY_CURRENCY_IN_STORE
  | typeof ALL_TRAVEL_MONEY_STORES;

type TStoreFinder = {
  stores: TFormatStore[];
  message: React.ReactNode;
  mapKey: string;
  serviceInformationModal: TInformationModal;
  defaultServiceOption?: TServiceOption;
  monetateTrackingId?: string;
  osakaChangesEnabled?: boolean;
};

export const StoreFinder = ({
  stores,
  message,
  mapKey,
  serviceInformationModal,
  defaultServiceOption,
  monetateTrackingId,
  osakaChangesEnabled = false,
}: TStoreFinder) => {
  const [coordinates, setCoordinates] = useState<TCoordinates | undefined>();
  const [selectedService, setSelectedService] = useState(defaultServiceOption);
  const [closestStores, setClosestStores] = useState([] as TStoreCards[]);
  const [visibleStores, setVisibleStores] = useState(3);
  const { isSmall, isMedium, isLarge, isExtraLarge } = useScreenSize();
  const descriptionRef = React.useRef<HTMLDivElement>(null);
  const serviceOverride = osakaChangesEnabled
    ? CLICK_AND_COLLECT
    : selectedService;

  useEffect(() => {
    const filterStoresByService = coordinates
      ? filterStores(stores, serviceOverride as TSelectedService)
      : [];

    if (coordinates) {
      setClosestStores(findClosestStores(coordinates, filterStoresByService));
      setVisibleStores(3);
    }

    setTimeout(() => {
      descriptionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  }, [coordinates, serviceOverride, stores]);

  const setCoords = (coords: {
    lat: number;
    lng: number;
    address?: string;
  }) => {
    setCoordinates(coords);
  };

  const handleUpdate = () => {
    setVisibleStores(6);
  };

  const address = coordinates?.address
    ? coordinates.address
    : coordinates?.lat
      ? `current location`
      : undefined;

  const renderMapSmall = closestStores.length > 0 && (isSmall || isMedium);
  const formattedClosestStores = mapFormatStores(closestStores).slice(
    0,
    visibleStores
  );

  return (
    <div
      className={classNames(style.wrapper, {
        [style.osakaChangesEnabledWrapper]: osakaChangesEnabled,
      })}
    >
      <div className={style.firstColumn}>
        <div className={style.form}>
          {osakaChangesEnabled ? (
            <GeoLocationInput
              onCoordinates={setCoords}
              monetateTrackingId={monetateTrackingId}
            />
          ) : (
            <DeprecatedGeoLocationInput
              onCoordinates={setCoords}
              onServiceChange={setSelectedService}
              serviceInformationModal={serviceInformationModal}
              defaultServiceOption={defaultServiceOption}
              monetateTrackingId={monetateTrackingId}
            />
          )}
        </div>
        {address && (
          <div className={style.description} ref={descriptionRef}>
            {visibleStores} <strong>Travel money</strong> stores nearest to ‘
            <strong>{address}</strong>’
          </div>
        )}
        {renderMapSmall &&
          (isSafari13OrLower() ? (
            <div className={style.mapMessage}>
              <MapUnavailableMessage />
            </div>
          ) : (
            <div className={style.mapSmall}>
              <MapLoader
                location={coordinates}
                stores={mapFormatStores(stores)}
                closestStores={formattedClosestStores}
                mapKey={mapKey}
              />
            </div>
          ))}
        {closestStores.length > 0 && (
          <StoreCards
            storeCards={closestStores}
            visibleStores={visibleStores}
            updateVisibleStores={handleUpdate}
            message={message}
            osakaChangesEnabled={osakaChangesEnabled}
          />
        )}
      </div>
      {(isLarge || isExtraLarge) &&
        (isSafari13OrLower() ? (
          <div className={style.mapMessage}>
            <MapUnavailableMessage />
          </div>
        ) : (
          <div
            className={classNames(style.mapLarge, {
              [style.mapIncrease]: closestStores.length > 0,
              [style.mapLargeOsakaChangesEnabled]: osakaChangesEnabled,
            })}
          >
            <MapLoader
              location={coordinates}
              stores={mapFormatStores(stores)}
              closestStores={formattedClosestStores}
              mapKey={mapKey}
            />
          </div>
        ))}
    </div>
  );
};

export default StoreFinder;
